import React, { useState } from "react";
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Input,
    Label,
    Spinner,
} from "reactstrap";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import formatCurrency from "constants/formatCurrency";

const ExportPlatformReport = ({ locations = [], type = "CorePark" }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedCols, setSelectedCols] = useState([]);
    const [loading, setLoading] = useState(false);

    const columns =
        type === "SpotHero"
            ? [
                { key: "location", label: "Location" },
                { key: "transactions", label: "Transactions" },
                { key: "gross_remit", label: "Gross Remit" },
                { key: "reservation_remit", label: "Reservation Remit" },
                { key: "oversize_fee_remit", label: "Oversize Fee Remit" },
                { key: "total_remit", label: "Total Remit" },
                { key: "total_sale_price", label: "Total Sale Price" },
            ]
            : type === "Resolve"
                ? [
                    { key: "location", label: "Location" },
                    { key: "transactions", label: "Transactions" },
                    { key: "total_amount", label: "Total Amount" },
                ]
                : type === "SurePark"
                    ? [
                        { key: "location", label: "Location" },
                        { key: "transactions", label: "Transactions" },
                        { key: "total_revenue", label: "Total Revenue" },
                    ]
                    : type === "Amano"
                        ? [
                            { key: "location", label: "Location" },
                            { key: "transactions", label: "Transactions" },
                            { key: "total_fee", label: "Total Fee" },
                            { key: "total_vat", label: "Total Vat" },
                            { key: "total_net", label: "Net" },
                        ]
                        : [
                            { key: "location", label: "Location" },
                            { key: "tickets", label: "Total CP Tickets" },
                            { key: "sq_transactions", label: "SQ Transactions" },
                            { key: "cash", label: "CP Cash" },
                            { key: "square_card", label: "SQ Card" },
                            { key: "total_parking", label: "CP + SQ Total Parking" },
                            { key: "square_tips", label: "SQ Tip" },
                            { key: "sq_trx_fee", label: "SQ Trx Fee" },
                            { key: "tax", label: "CP Card Tax" },
                            { key: "sq_processing_fee", label: "SQ Prcs. Fee" },
                            { key: "sq_refund", label: "SQ Refund & Return" },
                            { key: "sq_refund_trx_fee", label: "SQ Refund Trx Fee" },
                            { key: "sq_refund_card_tax", label: "SQ Refund Card Tax" },
                            { key: "gross_sales", label: "Net Sales" },
                        ];

    const toggleModal = () => setModalVisible(!modalVisible);

    const handleCheckboxChange = (key) => {
        setSelectedCols((prev) =>
            prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
        );
    };

    const handleSelectAll = (checked) => {
        if (checked) setSelectedCols(columns.map((c) => c.key));
        else setSelectedCols([]);
    };

    const buildExportData = () => {
        if (type === "Resolve") {
            return locations
                .filter((loc) => loc.transactions && loc.transactions.length > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((loc) => {
                    const totalAmount = loc.transactions.reduce(
                        (sum, t) =>
                            sum +
                            (((t.amount + t.fee - 0.4) -
                                (0.028 * (t.amount + t.fee)) -
                                0.3) || 0),
                        0
                    );

                    return {
                        location: loc.name,
                        transactions: loc.transactions.length,
                        total_amount: formatCurrency(totalAmount),
                    };
                });
        }

        if (type === "SpotHero") {
            return locations
                .filter((loc) => loc.transactions && loc.transactions.length > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((loc) => {
                    const totals = loc.transactions.reduce(
                        (acc, t) => {
                            acc.gross_remit += t.gross_remit || 0;
                            acc.reservation_remit += t.reservation_remit || 0;
                            acc.oversize_fee_remit += t.oversize_fee_remit || 0;
                            acc.total_remit += t.total_remit || 0;
                            acc.total_sale_price += t.total_sale_price || 0;
                            return acc;
                        },
                        {
                            gross_remit: 0,
                            reservation_remit: 0,
                            oversize_fee_remit: 0,
                            total_remit: 0,
                            total_sale_price: 0,
                        }
                    );

                    return {
                        location: loc.name,
                        transactions: loc.transactions.length,
                        gross_remit: formatCurrency(totals.gross_remit),
                        reservation_remit: formatCurrency(totals.reservation_remit),
                        oversize_fee_remit: formatCurrency(totals.oversize_fee_remit),
                        total_remit: formatCurrency(totals.total_remit),
                        total_sale_price: formatCurrency(totals.total_sale_price),
                    };
                });
        }

        if (type === "SurePark") {
            return locations
                .filter((loc) => loc.transactions && loc.transactions.length > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((loc) => {
                    const totalRevenue = loc.transactions.reduce(
                        (sum, t) => sum + (t.amount || 0),
                        0
                    );

                    return {
                        location: loc.name,
                        transactions: loc.transactions.length,
                        total_revenue: formatCurrency(totalRevenue),
                    };
                });
        }

        if (type === "Coast2Coast") {
            return locations
                .filter((loc) => loc.transactions && loc.transactions.length > 0)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((loc) => {
                    const totalRevenue = loc.transactions.reduce(
                        (sum, t) => sum + (t.amount || 0),
                        0
                    );

                    return {
                        location: loc.name,
                        transactions: loc.transactions.length,
                        total_revenue: formatCurrency(totalRevenue),
                    };
                });
        }

        // CorePark
        return locations
            .filter((loc) => loc.corepark && loc.corepark.tickets > 0)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((loc) => {
                const totals = loc.corepark;

                const square = loc.square || {
                    square_total: 0,
                    square_tips: 0,
                    total_trx_fee_amount: 0,
                    processing_fee_total: 0,
                    refunds_total: 0,
                    total_refund_fee_amount: 0,
                };

                const totalSquareCard =
                    square.square_total -
                    square.total_trx_fee_amount -
                    square.total_card_tax_amount;

                const netSales =
                    totals.cash +
                    totalSquareCard +
                    square.square_tips +
                    square.total_trx_fee_amount +
                    square.total_card_tax_amount -
                    square.total_refund_card_tax_amount -
                    (square.processing_fee_total / 100) -
                    square.refunds_total -
                    square.total_refund_fee_amount;

                return {
                    location: loc.name,
                    tickets: totals.tickets,
                    sq_transactions: square.transactions,
                    cash: formatCurrency(totals.cash),
                    square_card: formatCurrency(totalSquareCard),
                    total_parking: formatCurrency(totals.cash + totalSquareCard),
                    square_tips: formatCurrency(square.square_tips),
                    sq_trx_fee: formatCurrency(square.total_trx_fee_amount),
                    tax: formatCurrency(square.total_card_tax_amount),
                    sq_processing_fee: formatCurrency(square.processing_fee_total / 100),
                    sq_refund: formatCurrency(square.refunds_total - square.total_refund_fee_amount),
                    sq_refund_trx_fee: formatCurrency(square.total_refund_fee_amount),
                    sq_refund_card_tax: formatCurrency(square.total_refund_card_tax_amount),
                    gross_sales: formatCurrency(netSales),
                };
            });
    };

    const exportToExcel = async () => {
        setLoading(true);
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(`${type} Report`);

        const selectedColumns =
            selectedCols.length > 0
                ? columns.filter((col) => selectedCols.includes(col.key))
                : columns;

        // Header
        sheet.addRow(selectedColumns.map((col) => col.label));

        // Data
        const rows = buildExportData();
        rows.forEach((row) => {
            sheet.addRow(selectedColumns.map((col) => row[col.key]));
        });

        // Widths
        sheet.columns.forEach((col) => {
            let maxLength = 10;
            col.eachCell({ includeEmpty: true }, (cell) => {
                const val = cell.value ? cell.value.toString() : "";
                if (val.length > maxLength) maxLength = val.length;
            });
            col.width = Math.min(maxLength + 2, 50);
        });

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buf]),
            `${type}_Report_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        setLoading(false);
        setModalVisible(false);
    };

    return (
        <>
            <Button
                className="button-gradient-bg"
                color="primary"
                size="sm"
                onClick={toggleModal}
                style={{ display: "block", margin: "0 auto" }}
            >
                Export
            </Button>

            <Modal isOpen={modalVisible} toggle={toggleModal} size="md">
                <ModalHeader toggle={toggleModal}>Select Columns to Export</ModalHeader>
                <ModalBody>
                    <div className="mb-2">
                        <Label check className="fw-bold">
                            <Input
                                id="selectAll"
                                type="checkbox"
                                checked={selectedCols.length === columns.length}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                            />{" "}
                            Select All
                        </Label>
                    </div>

                    {columns.map((col) => (
                        <Label
                            key={col.key}
                            check
                            className="d-flex align-items-center mb-2"
                            htmlFor={`col-${col.key}`}
                        >
                            <Input
                                id={`col-${col.key}`}
                                type="checkbox"
                                checked={selectedCols.includes(col.key)}
                                onChange={() => handleCheckboxChange(col.key)}
                            />{" "}
                            <span className="ms-2">{col.label}</span>
                        </Label>
                    ))}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                    <Button color="success" onClick={exportToExcel} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Export CSV"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ExportPlatformReport;